/**
 * EVENTSFLO-AI-SHIFT-RULES-TENANT-ADMIN-MANAGEMENT-V1
 *
 * Tenant-Admin-managed AI Shift Rules.
 *
 * Business name (page title): "Operational Rules for AI Project & Shift Interpretation"
 * Settings Hub label: "AI Rules"
 *
 * What this page does:
 *  - Lists the tenant's current operational rules.
 *  - Lets admins create / edit / enable / disable / delete rules in plain text.
 *  - Lets admins seed from the markdown KB the first time.
 *  - Lets admins paste a free-text request and view the AI DRAFT interpretation
 *    (REVIEW-ONLY — no project/shift is created).
 *
 * What this page DOES NOT do:
 *  - Replace the live config-rule system (db.rules / db.rule_variables /
 *    db.task_rule_configs / evaluate_shift_rules / allocation_service).
 *  - Persist any AI draft.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Wand2,
  ChevronLeft,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  Info,
  ShieldAlert,
  HelpCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyRule = () => ({
  id: null,
  rule_id: "",
  rule_name: "",
  scope: "shift",
  trigger_text: "",
  action_text: "",
  exceptions_text: "",
  priority: 50,
  example: "",
  active: true,
});

function authHeaders(extra = {}) {
  const token = localStorage.getItem("eventsflo_auth_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

export default function AIShiftRulesPage() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editor, setEditor] = useState(null); // active rule being edited or created

  // AI draft tester
  const [draftText, setDraftText] = useState("");
  const [draft, setDraft] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);

  // EVENTSFLO-AI-RULES-HEALTH-CHECK-V1: rule-health warnings
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);

  // EVENTSFLO-AI-RULES-HELP-MODAL-V1
  const [helpOpen, setHelpOpen] = useState(false);

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50)),
    [rules]
  );

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-shift-rules`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed to load rules (${res.status})`);
      const data = await res.json();
      setRules(data.rules || []);
    } catch (e) {
      toast.error(e.message || "Failed to load rules");
    } finally {
      setLoading(false);
    }
    // EVENTSFLO-AI-RULES-HEALTH-CHECK-V1: refresh health after rules change
    runHealth(true);
  };

  // EVENTSFLO-AI-RULES-HEALTH-CHECK-V1
  const runHealth = async (silent = false) => {
    setHealthBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-shift-rules/health`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Health check failed (${res.status})`);
      const data = await res.json();
      setHealth(data);
      if (!silent) {
        const total = (data.summary?.error || 0) + (data.summary?.warning || 0) + (data.summary?.info || 0);
        toast.success(total === 0 ? "Rules look healthy" : `${total} rule warning(s) found`);
      }
    } catch (e) {
      if (!silent) toast.error(e.message || "Health check failed");
    } finally {
      setHealthBusy(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const seedFromMarkdown = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-shift-rules/seed-from-markdown`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Seed failed (${res.status})`);
      const data = await res.json();
      toast.success(`Seeded ${data.inserted} rule(s) (skipped ${data.skipped})`);
      await loadRules();
    } catch (e) {
      toast.error(e.message || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const openCreate = () => setEditor(emptyRule());
  const openEdit = (rule) => setEditor({ ...rule });

  const saveEditor = async () => {
    if (!editor) return;
    if (!editor.rule_id?.trim() || !editor.rule_name?.trim()) {
      toast.error("Rule ID and Name are required");
      return;
    }
    setSaving(true);
    try {
      let res, data;
      if (editor.id) {
        // Update
        const body = {
          rule_name: editor.rule_name,
          scope: editor.scope,
          trigger_text: editor.trigger_text,
          action_text: editor.action_text,
          exceptions_text: editor.exceptions_text,
          priority: Number(editor.priority) || 50,
          example: editor.example,
          active: !!editor.active,
        };
        res = await fetch(`${API_URL}/api/ai-shift-rules/${editor.id}`, {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
      } else {
        // Create
        const body = {
          rule_id: editor.rule_id.trim(),
          rule_name: editor.rule_name.trim(),
          scope: editor.scope,
          trigger_text: editor.trigger_text || "",
          action_text: editor.action_text || "",
          exceptions_text: editor.exceptions_text || "",
          priority: Number(editor.priority) || 50,
          example: editor.example || "",
          active: !!editor.active,
        };
        res = await fetch(`${API_URL}/api/ai-shift-rules`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) {
        data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Save failed (${res.status})`);
      }
      toast.success(editor.id ? "Rule updated" : "Rule created");
      setEditor(null);
      await loadRules();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      const res = await fetch(`${API_URL}/api/ai-shift-rules/${rule.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ active: !rule.active }),
      });
      if (!res.ok) throw new Error(`Toggle failed (${res.status})`);
      await loadRules();
    } catch (e) {
      toast.error(e.message || "Toggle failed");
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.rule_name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/ai-shift-rules/${rule.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      toast.success("Rule deleted");
      await loadRules();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  const runDraft = async () => {
    if (!draftText.trim()) {
      toast.error("Paste a free-text request first");
      return;
    }
    setDraftBusy(true);
    setDraft(null);
    try {
      const res = await fetch(`${API_URL}/api/ai-shift-rules/draft`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ text: draftText }),
      });
      if (!res.ok) throw new Error(`Draft failed (${res.status})`);
      const data = await res.json();
      setDraft(data);
    } catch (e) {
      toast.error(e.message || "Draft failed");
    } finally {
      setDraftBusy(false);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      {/* Breadcrumb / back */}
      <button
        onClick={() => navigate("/settings")}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3"
        data-testid="back-to-settings-hub"
      >
        <ChevronLeft className="h-3 w-3" />
        Settings Hub
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-pink-500" />
            Operational Rules for AI Project &amp; Shift Interpretation
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Manage the operational rules the AI uses when interpreting incoming project and shift
            requests. Edit the wording the way you would explain it to a new ops manager; the AI
            cites every rule it applies.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* EVENTSFLO-AI-RULES-HELP-MODAL-V1 */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHelpOpen(true)}
            data-testid="ai-rules-help"
          >
            <HelpCircle className="h-4 w-4 mr-1" />
            Help
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadRules}
            disabled={loading}
            data-testid="ai-rules-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => runHealth(false)}
            disabled={healthBusy}
            data-testid="ai-rules-health"
          >
            <ShieldAlert className={`h-4 w-4 mr-1 ${healthBusy ? "animate-spin" : ""}`} />
            Health check
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={seedFromMarkdown}
            disabled={seeding}
            data-testid="ai-rules-seed"
          >
            <Wand2 className="h-4 w-4 mr-1" />
            {seeding ? "Seeding…" : "Seed from defaults"}
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="ai-rules-create">
            <Plus className="h-4 w-4 mr-1" />
            New rule
          </Button>
        </div>
      </div>

      {/* EVENTSFLO-SETTINGS-RULES-LABEL-ACTIVE-VS-AI-REVIEW-ONLY-V1
         Impossible-to-miss banner. AI Rules are not used by live saved-shift
         operations. Live behaviour is controlled by Live Rule Settings,
         Task Definitions, and the deterministic backend rules engine. */}
      <Card
        className="mb-6 border-amber-500/40 bg-amber-500/5"
        data-testid="ai-rules-review-only-banner"
      >
        <CardContent className="py-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <div className="font-semibold text-amber-700 dark:text-amber-300 mb-1">
              REVIEW ONLY — not used by saved shifts
            </div>
            <p className="text-foreground/90">
              These AI Rules are <strong>not currently used</strong> by live shift
              creation, editing, copying, crew assignment, pricing, or supplements.
              Live saved-shift behaviour is controlled by{" "}
              <button
                type="button"
                onClick={() => navigate("/rules")}
                className="underline underline-offset-2 hover:text-primary"
                data-testid="ai-rules-banner-live-link"
              >
                Live Rule Settings
              </button>
              , Task Definitions, and backend deterministic rules.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* EVENTSFLO-AI-RULES-HEALTH-CHECK-V1: warnings panel */}
      {health && (health.warnings?.length > 0) && (
        <Card className="mb-6 border-amber-500/20" data-testid="ai-rules-health-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-400" />
              Rule Health Warnings
              <Badge variant="outline" className="text-[10px]">
                {health.warnings.length} item{health.warnings.length === 1 ? "" : "s"}
              </Badge>
              <span className="ml-auto flex items-center gap-1 text-[11px] font-normal text-muted-foreground">
                {health.summary?.error > 0 && (
                  <Badge className="text-[10px] bg-red-500/20 text-red-300">
                    {health.summary.error} error
                  </Badge>
                )}
                {health.summary?.warning > 0 && (
                  <Badge className="text-[10px] bg-amber-500/20 text-amber-300">
                    {health.summary.warning} warning
                  </Badge>
                )}
                {health.summary?.info > 0 && (
                  <Badge className="text-[10px] bg-sky-500/20 text-sky-300">
                    {health.summary.info} info
                  </Badge>
                )}
              </span>
            </CardTitle>
            <CardDescription>
              Non-blocking checks. Tenant rules sharing a built-in seed
              <code className="px-1 mx-1 rounded bg-muted/40 text-[11px]">rule_id</code>
              take precedence over the seed version.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {health.warnings.map((w, i) => {
              const Icon =
                w.severity === "error"
                  ? AlertTriangle
                  : w.severity === "warning"
                  ? ShieldAlert
                  : Info;
              const color =
                w.severity === "error"
                  ? "text-red-400"
                  : w.severity === "warning"
                  ? "text-amber-400"
                  : "text-sky-400";
              return (
                <div
                  key={i}
                  className="flex items-start gap-3 p-2 rounded border border-white/5 bg-surface-base/40"
                  data-testid={`ai-rule-warning-${w.code}`}
                >
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {w.code.replace(/_/g, " ")}
                      </Badge>
                      {(w.rule_ids || []).map((rid) => (
                        <Badge
                          key={rid}
                          variant="outline"
                          className="text-[10px] font-mono"
                        >
                          {rid}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {w.message}
                    </p>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Rules list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            Tenant Rules
            <Badge variant="outline" className="text-xs">
              {rules.length} rule{rules.length === 1 ? "" : "s"}
            </Badge>
          </CardTitle>
          <CardDescription>
            Lower priority numbers run first. Disabled rules are skipped by the AI.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sortedRules.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-10">
              No rules yet for this tenant. Click <strong>Seed from defaults</strong> to start
              from the EventsFlo defaults, or <strong>New rule</strong> to write your own.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {sortedRules.map((r) => (
                <div
                  key={r.id}
                  className="py-3 flex items-start gap-3"
                  data-testid={`ai-rule-row-${r.rule_id}`}
                >
                  <button
                    onClick={() => toggleActive(r)}
                    className="mt-1 text-muted-foreground hover:text-foreground"
                    title={r.active ? "Disable rule" : "Enable rule"}
                    data-testid={`ai-rule-toggle-${r.rule_id}`}
                  >
                    {r.active ? (
                      <ToggleRight className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm">{r.rule_name}</span>
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {r.rule_id}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {r.scope}
                      </Badge>
                      {r.scope === "parser" && (
                        <Badge
                          className="text-[10px] bg-sky-500/15 text-sky-300 border border-sky-500/30"
                          data-testid={`ai-rule-parser-doc-only-${r.rule_id}`}
                          title="Parser visibility rule — shown here for transparency. Editing does not yet change parser behaviour."
                        >
                          documentation only
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">
                        priority {r.priority}
                      </Badge>
                      {!r.active && (
                        <Badge className="text-[10px] bg-muted/40 text-muted-foreground">
                          disabled
                        </Badge>
                      )}
                    </div>
                    {r.trigger_text && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        <span className="font-medium text-foreground/70">Trigger:</span>{" "}
                        {r.trigger_text}
                      </p>
                    )}
                    {r.action_text && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        <span className="font-medium text-foreground/70">Action:</span>{" "}
                        {r.action_text}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(r)}
                      data-testid={`ai-rule-edit-${r.rule_id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteRule(r)}
                      className="text-red-400"
                      data-testid={`ai-rule-delete-${r.rule_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Draft tester */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Test AI draft interpretation</CardTitle>
          <CardDescription>
            Paste a real-world request below. The AI will return a structured draft citing the
            applied rule IDs. <strong>Nothing is created or saved.</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder="e.g. AON Service install at Grimaldi Forum on 12 May 2026, 08:00 to 18:00. 6 stagehands and 1 rigger. Venue 45km out of town. No client transport. No client meals."
            className="min-h-[120px]"
            data-testid="ai-draft-input"
          />
          <div className="flex items-center justify-end">
            <Button onClick={runDraft} disabled={draftBusy} data-testid="ai-draft-run">
              <Sparkles className="h-4 w-4 mr-1" />
              {draftBusy ? "Interpreting…" : "Run AI draft"}
            </Button>
          </div>

          {draft && (
            <div className="border border-white/5 rounded-lg p-4 mt-2 bg-surface-base/40 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  model: {draft.model_used || "—"}
                </Badge>
                <Badge
                  className={`text-[10px] ${
                    draft.confidence >= 0.7
                      ? "bg-emerald-500/20 text-emerald-300"
                      : draft.confidence >= 0.4
                      ? "bg-amber-500/20 text-amber-300"
                      : "bg-red-500/20 text-red-300"
                  }`}
                >
                  confidence {Math.round((draft.confidence || 0) * 100)}%
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  shifts: {draft.shifts?.length || 0}
                </Badge>
              </div>

              {draft.applied_rule_ids?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  <span className="text-xs text-muted-foreground mr-1">Cited rules:</span>
                  {draft.applied_rule_ids.map((rid) => (
                    <Badge key={rid} variant="outline" className="text-[10px] font-mono">
                      {rid}
                    </Badge>
                  ))}
                </div>
              )}

              {draft.ambiguities?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-amber-400 mb-1">Ambiguities</p>
                  <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
                    {draft.ambiguities.map((a, i) => (
                      <li key={i}>
                        <span className="text-foreground/80">{a.field}:</span> {a.question}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {draft.assumptions?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Assumptions</p>
                  <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
                    {draft.assumptions.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}

              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Full draft JSON</summary>
                <pre className="mt-2 max-h-72 overflow-auto bg-black/40 p-2 rounded text-[10px]">
                  {JSON.stringify(draft, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor dialog */}
      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-2xl" data-testid="ai-rule-editor">
          <DialogHeader>
            <DialogTitle>{editor?.id ? "Edit rule" : "New rule"}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-1">
                <Label className="text-xs">Rule ID</Label>
                <Input
                  value={editor.rule_id}
                  onChange={(e) =>
                    setEditor({ ...editor, rule_id: e.target.value })
                  }
                  placeholder="RULE-CUSTOM-XYZ-001"
                  disabled={!!editor.id}
                  data-testid="ai-rule-input-rule_id"
                />
              </div>
              <div className="md:col-span-1">
                <Label className="text-xs">Rule name</Label>
                <Input
                  value={editor.rule_name}
                  onChange={(e) =>
                    setEditor({ ...editor, rule_name: e.target.value })
                  }
                  placeholder="VIP Crew Polo Required"
                  data-testid="ai-rule-input-rule_name"
                />
              </div>

              <div>
                <Label className="text-xs">Scope</Label>
                <Select
                  value={editor.scope}
                  onValueChange={(v) => setEditor({ ...editor, scope: v })}
                >
                  <SelectTrigger data-testid="ai-rule-input-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shift">shift</SelectItem>
                    <SelectItem value="project">project</SelectItem>
                    <SelectItem value="parser">parser (documentation-only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority (lower runs first)</Label>
                <Input
                  type="number"
                  value={editor.priority}
                  min={0}
                  max={1000}
                  onChange={(e) =>
                    setEditor({ ...editor, priority: e.target.value })
                  }
                  data-testid="ai-rule-input-priority"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs">When to apply (Trigger)</Label>
                <Textarea
                  value={editor.trigger_text || ""}
                  onChange={(e) =>
                    setEditor({ ...editor, trigger_text: e.target.value })
                  }
                  placeholder="Plain English: e.g. Shift duration ≥ 6 hours and meals are not provided by the client."
                  className="min-h-[64px]"
                  data-testid="ai-rule-input-trigger"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">What to do (Action)</Label>
                <Textarea
                  value={editor.action_text || ""}
                  onChange={(e) =>
                    setEditor({ ...editor, action_text: e.target.value })
                  }
                  placeholder="Plain English: e.g. Add a meal allowance line for the full crew."
                  className="min-h-[64px]"
                  data-testid="ai-rule-input-action"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Exceptions</Label>
                <Textarea
                  value={editor.exceptions_text || ""}
                  onChange={(e) =>
                    setEditor({ ...editor, exceptions_text: e.target.value })
                  }
                  placeholder="Plain English: e.g. Skip if the client confirms catering is on-site."
                  className="min-h-[48px]"
                  data-testid="ai-rule-input-exceptions"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Example</Label>
                <Textarea
                  value={editor.example || ""}
                  onChange={(e) =>
                    setEditor({ ...editor, example: e.target.value })
                  }
                  placeholder="Plain English example to anchor the AI."
                  className="min-h-[48px]"
                  data-testid="ai-rule-input-example"
                />
              </div>

              <div className="md:col-span-2 flex items-center gap-2">
                <input
                  id="rule-active"
                  type="checkbox"
                  checked={!!editor.active}
                  onChange={(e) =>
                    setEditor({ ...editor, active: e.target.checked })
                  }
                  data-testid="ai-rule-input-active"
                />
                <Label htmlFor="rule-active" className="text-xs">
                  Active (AI will use this rule)
                </Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditor(null)}
              disabled={saving}
              data-testid="ai-rule-cancel"
            >
              Cancel
            </Button>
            <Button onClick={saveEditor} disabled={saving} data-testid="ai-rule-save">
              <Save className="h-4 w-4 mr-1" />
              {saving ? "Saving…" : "Save rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* EVENTSFLO-AI-RULES-HELP-MODAL-V1 */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-rules-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-pink-400" />
              How AI Rules work
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 text-sm leading-relaxed">
            <section>
              <h3 className="text-base font-semibold mb-1">What is this page?</h3>
              <p className="text-muted-foreground">
                This is where you write the operational rules the AI uses when
                interpreting an incoming project or shift request — for example
                whether a job needs a crew chief, whether meal allowances apply,
                or whether transport / accommodation should be added.
              </p>
            </section>

            <section className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-amber-400" />
                These rules are review-only
              </h3>
              <p className="text-muted-foreground">
                Rules here <strong>do not</strong> automatically create or change
                projects or shifts. They power the <em>AI draft</em> shown in
                the "Test AI draft interpretation" card at the bottom of the page.
                A human always reviews the draft before anything is saved.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold mb-1">What still controls live project &amp; shift behaviour?</h3>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                <li>The existing live shift rules engine (e.g. crew-chief threshold, meal break threshold, late-night windows).</li>
                <li>The crew allocation engine (qualifications, expiry checks, conflicts).</li>
                <li>The numeric rule variables managed by ops (meal rate, kilometre rate, etc.).</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                Editing rules on this page <strong>never</strong> changes any of
                the above. They run side-by-side with the AI prototype.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold mb-1">Rule precedence in plain language</h3>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                <li>EventsFlo ships built-in <em>seed rules</em> (e.g. <code className="text-foreground/80">RULE-MEAL-001</code>).</li>
                <li>If your tenant creates a rule with the <strong>same Rule ID</strong>, your version wins. The Health check flags this with the
                  <Badge variant="outline" className="text-[10px] mx-1">OVERRIDES SEED RULE</Badge>
                  warning so the override is never silent.</li>
                <li>Lower priority numbers run first. Two active rules sharing
                  the same priority on the same scope are flagged with
                  <Badge variant="outline" className="text-[10px] mx-1">DUPLICATE PRIORITY</Badge>
                  because the run-order between them is undefined.</li>
                <li>Disabled rules (toggle off) are simply skipped by the AI.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-semibold mb-1">How to write a good rule</h3>
              <ol className="list-decimal pl-5 text-muted-foreground space-y-1">
                <li><strong>Rule ID</strong> — short, stable, capitalised, hyphen-separated. Example: <code className="text-foreground/80">RULE-VIP-POLO-001</code>.</li>
                <li><strong>Rule name</strong> — what you'd say out loud. "VIP Crew Polo Required".</li>
                <li><strong>Trigger</strong> — when should this fire? Be specific. "Client tier is VIP and shift is on the show floor."</li>
                <li><strong>Action</strong> — what should the AI propose? "Set uniform = VIP polo and ensure the crew chief carries a radio."</li>
                <li><strong>Exceptions</strong> — when to skip. "Skip if the client confirms catering on-site." (optional but helpful)</li>
                <li><strong>Example</strong> — one realistic scenario. Improves AI accuracy materially.</li>
                <li><strong>Priority</strong> — lower runs first; cluster related rules close together.</li>
              </ol>
            </section>

            <section>
              <h3 className="text-base font-semibold mb-1">What do the Health warnings mean?</h3>
              <ul className="text-muted-foreground space-y-1">
                <li><Badge variant="outline" className="text-[10px] mr-1">MISSING TRIGGER</Badge> — the rule has no Trigger text. The AI can't apply it reliably.</li>
                <li><Badge variant="outline" className="text-[10px] mr-1">MISSING ACTION</Badge> — the rule has no Action text. The AI doesn't know what to propose.</li>
                <li><Badge variant="outline" className="text-[10px] mr-1">MISSING EXAMPLE</Badge> — informational. Adding an example improves AI accuracy.</li>
                <li><Badge variant="outline" className="text-[10px] mr-1">WEAK WORDING</Badge> — the trigger or action is fewer than 5 words. Be more specific.</li>
                <li><Badge variant="outline" className="text-[10px] mr-1">DUPLICATE PRIORITY</Badge> — two active rules at the same priority on the same scope. Re-rank one.</li>
                <li><Badge variant="outline" className="text-[10px] mr-1">LIKELY DUPLICATE</Badge> — two rules look very similar. Probably one of them should go.</li>
                <li><Badge variant="outline" className="text-[10px] mr-1">POTENTIAL CONFLICT</Badge> — two rules target the same kind of situation but propose different actions. Confirm they don't conflict.</li>
                <li><Badge variant="outline" className="text-[10px] mr-1">OVERRIDES SEED RULE</Badge> — your rule shadows a built-in rule. Make sure that's intentional.</li>
                <li><Badge variant="outline" className="text-[10px] mr-1">NAME MATCHES SEED</Badge> — your rule has the same name as a built-in rule but a different ID. Both end up in the AI prompt — consider renaming or disabling one.</li>
              </ul>
            </section>

            <section className="text-xs text-muted-foreground border-t border-white/5 pt-3">
              Health warnings are <strong>non-blocking</strong>. Saving a rule
              never fails because of a warning — they're guidance, not gates.
            </section>
          </div>

          <DialogFooter>
            <Button onClick={() => setHelpOpen(false)} data-testid="ai-rules-help-close">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
